import React, { useRef, useEffect, useMemo, useCallback } from 'react'
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import { 
    horarioPrevistoPonto,
    getBgColorByStatus,
    getColorByStatus,
    getDiferencaKmRota
} from '../utils/tripUtils';
import { formatHora } from '../utils/dateUtils';
import { useMapData } from '../hooks/map/useMapData';
import { useMapActions } from '../hooks/map/useMapActions';
import { usePontos } from '../hooks/pontos/usePontos';
import { useTripStatusAnalysis } from '../hooks/analysis/useTripStatusAnalysis';
import { useTripPositionsAnalysis } from '../hooks/analysis/useTripPositionsAnalysis';
import { useKmTotalRota } from '../hooks/lines/useKmTotalRota';
import TripStatusSummary from './TripStatusSummary';

export default function TripPointsPanel({
    trip = null,
    warningVehicle
}) {

    const mapData = useMapData();
    const currentTrip = trip ?? mapData.trip;
    const tripUidMap = mapData.tripUidMap;
    const openMap = mapData.openMap;
    const selectedPoint = mapData.selectedPoint;

    const pontos = usePontos(currentTrip?.CODIGO_INTEGRACAO);
    const tripStatus = useTripStatusAnalysis(
        currentTrip?.DATA_REFERENCIA,
        currentTrip?.CODIGO_INTEGRACAO,
        currentTrip?.DATAHORA_FINAL_PREVISTO
    );
    const situacao = tripStatus?.situacao;
    const barColor = tripStatus?.barColor;
    const detaTrip = tripStatus?.detaTrip;
    const statusTrip = tripStatus?.statusTrip;

    const { pointClick, openMapAndPoint } = useMapActions();

    const isMapMode = trip == null;

    const positionsAnalysis = useTripPositionsAnalysis(currentTrip?.DATA_REFERENCIA, currentTrip?.CODIGO_INTEGRACAO);
    const pontosAnalysis = positionsAnalysis?.resultado;
    const progressoPercurso = positionsAnalysis?.progressoPercurso ?? 0;
    const progressoPontos = positionsAnalysis?.progressoPontos ?? 0;
    const totalP = pontos.length;
    const atendP = positionsAnalysis?.atendP ?? 0;
    const hrInicio = tripStatus?.hrInicio ?? null;
    const DATAHORA_FINAL_PREVISTO = 
        tripStatus?.DATAHORA_FINAL_PREVISTO ?? currentTrip?.DATAHORA_FINAL_PREVISTO;

    const kmsRodados = positionsAnalysis?.kmsRodados ?? 0;

    const kmTotalRota =
        useKmTotalRota(
            currentTrip?.CODIGO_INTEGRACAO
        );

    const diferencaKm =
        getDiferencaKmRota(
            kmsRodados,
            kmTotalRota
        );

    const tripData = {
        placa: currentTrip?.license_plate,
        startAt: currentTrip?.time_caught || currentTrip?.DATAHORA_INICIAL_PREVISTO,
        endAt: DATAHORA_FINAL_PREVISTO,
        sentido: currentTrip?.SENTIDO,
        TRIP_UID: currentTrip?.TRIP_UID
    };

    const lastPassedSeq = useMemo(() => {

        const last = [...pontos]
            .reverse()
            .find(ponto => {

                const analysis = pontosAnalysis?.[ponto.PONTO_REFERENCIA_ID];

                return (
                    analysis?.passou &&
                    analysis?.horarioPassagem
                );
            });

        return last?.PONTO_UID ?? null;

    }, [pontos, pontosAnalysis]);

    const scrollRef = useRef(null);
    const userScrollingRef = useRef(false);
    const pointRefs = useRef({});

    const lastAutoScrollSeqRef = useRef(null);
    const rafScrollRef = useRef(null);

    const scrollToLast = useCallback((behavior = 'auto') => {
        //deixar assim por enquanto
        //se quiserem que volte não precisa refazer
        return;
        // eslint-disable-next-line
        const container = scrollRef.current;
        const last = pointRefs.current[lastPassedSeq];

        if (!container || !last || situacao !== 'Em andamento') return;

        const seq = last.dataset.seq;

        if (behavior === 'auto') {
            if (lastAutoScrollSeqRef.current === seq) return;
            lastAutoScrollSeqRef.current = seq;
        }

        const offsetTop = last.offsetTop;
        const offsetHeight = last.offsetHeight;

        const scrollTo =
            offsetTop - container.clientHeight / 2 + offsetHeight / 2;

        container.scrollTo({
            top: scrollTo,
            behavior
        });
    }, [situacao, lastPassedSeq]);

    useEffect(() => {
        if (userScrollingRef.current) return;

        if (rafScrollRef.current) return;

        rafScrollRef.current = requestAnimationFrame(() => {
            scrollToLast('auto');
            rafScrollRef.current = null;
        });

    }, [pontos, scrollToLast]);

    useEffect(() => {

        if (!selectedPoint) return;

        const elem = pointRefs.current[selectedPoint.PONTO_UID];

        if (!elem) return;

        elem.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });

    }, [selectedPoint]);

    return (
        <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flex: '1 80%', 
            padding: 0,
            minHeight: isMapMode ? '100%' : undefined,
            maxHeight: isMapMode ? '100%' : undefined
        }}>
            {!isMapMode && (
                <>
                <TripStatusSummary
                    statusTrip={statusTrip}
                    barColor={barColor}
                    situacao={situacao}
                    detaTrip={detaTrip}
                    hrInicio={hrInicio}
                    atendP={atendP}
                    totalP={totalP}
                    progressoPontos={progressoPontos}
                    kmsRodados={kmsRodados}
                    kmTotalRota={kmTotalRota}
                    diferencaKm={diferencaKm}
                />
                {warningVehicle &&
                    <Tooltip title={`Veículo: ${warningVehicle} não encontrado ou inválido.`}>
                        <span>
                            <Box
                                sx={(theme) => ({
                                    borderColor: theme.palette.divider,
                                    borderRadius: 1,
                                    display: 'flex',
                                    gap: 1,
                                    p: 0.5,
                                    mb: 1,
                                    alignItems: 'center',
                                    color: theme.palette['warning']?.contrastText,
                                    backgroundColor: theme.palette['warning']?.main
                                })}
                            >
                                <DirectionsBusIcon sx={{ fontSize: 20 }} />
                                <Typography variant='subtitle1'>
                                    {warningVehicle}
                                </Typography>
                            </Box>
                        </span>
                    </Tooltip>
                }     
                </>
            )}
            
            <Box
                ref={scrollRef}
                onMouseEnter={() => {
                    userScrollingRef.current = true;
                }}
                onMouseLeave={() => {
                    userScrollingRef.current = false;
                    scrollToLast('smooth');
                }}
                sx={(theme) => ({
                    minHeight: isMapMode ? undefined : 240,
                    maxHeight: isMapMode ? undefined : 240,
                    overflowY: 'auto',
                    overflowX :'hidden',
                    marginY: isMapMode ? 0 : 2,
                    border: '1px solid',
                    borderRadius: isMapMode ? 0 : 1,
                    borderWidth: isMapMode ? 0 : 2,
                    position: 'relative',
                    borderColor:
                        barColor &&
                        theme.palette[barColor]
                            ? theme.palette[barColor].main
                            : theme.palette.divider
                })}
            >

                {pontos.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ padding: 1 }}>
                        --
                    </Typography>
                )}

                {/* ================================= */}
                {/* CABEÇALHO */}
                {/* ================================= */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 68px 68px 200px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        paddingY: 0.5,
                        paddingX: 1,
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="caption" sx={{fontWeight: 600}}>
                        Ponto
                    </Typography>

                    {/* PREVISTO */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center'
                        }}
                    >
                        <Tooltip title="Chegada Prevista">
                            <AlarmOnIcon
                                sx={(theme) => ({
                                    fontSize: 18,
                                    color: theme.palette.secondary.main
                                })}
                            />
                        </Tooltip>
                    </Box>

                    {/* REAL */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center'
                        }}
                    >
                        <Tooltip title="Chegada Real">
                            <AlarmOnIcon
                                sx={(theme) => ({
                                    fontSize: 18,
                                    color: theme.palette.success.main
                                })}
                            />
                        </Tooltip>
                    </Box>

                    {/* STATUS */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center'
                        }}
                    >
                        <Tooltip title="Status do Ponto">
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent:'flex-end',
                                    alignItems: 'center'
                                }}
                            >
                                <AssessmentIcon
                                    sx={(theme) => ({
                                        fontSize: 18,
                                        color: theme.palette.primary.main
                                    })}
                                />
                                <Typography
                                    variant="body2"
                                    align="right"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        fontSize: 16
                                    }}
                                >
                                    Status
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                {/* ================================= */}
                {/* LINHAS */}
                {/* ================================= */}
                {pontos.map((ponto, idx) => {
                    const isLastPoint = idx === pontos?.length - 1;
                    const analysis = pontosAnalysis?.[ponto.PONTO_REFERENCIA_ID];

                    const passou = analysis?.passou && analysis?.horarioPassagem;
                    const passouInferido = analysis?.passouInferido === true;

                    const horarioPassagem =
                        passou ? formatHora(analysis.horarioPassagem, 'HH:mm') : '--:--';

                    const statusPonto = analysis?.statusPonto ?? 0;

                    const pontoReferencia = ponto?.pontoReferencia;

                    const pontoDesc = `${(ponto.SEQUENCIA + 1)}. ${pontoReferencia.NOME ?? 'Ponto'}`;

                    const labelPonto = analysis?.labelPonto;

                    const isSelected =
                        selectedPoint?.PONTO_UID === ponto.PONTO_UID &&
                        tripUidMap === currentTrip?.TRIP_UID;

                    const pointTooltip =
                        isSelected && !isMapMode
                            ? `Vendo ${pontoDesc} no mapa.`
                            : !isMapMode
                                ? `Clique para ver ${pontoDesc} no mapa.`
                                : '';

                    return (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                if(openMap){
                                    pointClick(ponto);
                                }else {
                                    openMapAndPoint(
                                        currentTrip?.CODIGO_INTEGRACAO, ponto, tripData);
                                }
                                
                            }}
                            data-seq={ponto.SEQUENCIA}
                            ref={(el) => {

                                if (!el) {
                                    return;
                                }

                                pointRefs.current[
                                    ponto.PONTO_UID
                                ] = el;
                            }}
                            key={`${ponto.PONTO_UID}-${idx}`}
                            sx={(theme) => ({
                                display: 'grid',
                                gridTemplateColumns: '1fr 68px 68px 200px',
                                alignItems: 'center',
                                paddingY: 0.3,
                                paddingX: 1,
                                borderTop: openMap && isSelected
                                    ? '2px solid' : '0',
                                borderBottom: openMap && isSelected
                                    ? '2px solid' : '1px dashed',
                                borderColor: openMap && isSelected
                                    ? 'primary' : 'divider',
                                fontSize: 13,
                                transition: 'background-color 0.4s ease',
                                color: getColorByStatus(statusPonto, situacao, theme),
                                backgroundColor: getBgColorByStatus(statusPonto, situacao, theme),
                                cursor: 'pointer',
                                transform: openMap && isSelected
                                    ? 'scale(1.01)' : 'scale(1)',
                                zIndex: isSelected ? 2 : 1

                            })}
                        >
                            {/* PONTO */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}
                            >
                                {passou ? (

                                    <CheckCircleIcon
                                        sx={(theme) => ({
                                            fontSize: 22,
                                            border: 2,
                                            borderColor: passouInferido ? 
                                                theme.palette.inferred.main :
                                                theme.palette.success.main,
                                            borderRadius: '100%',
                                            backgroundColor: 'white',
                                            color: passouInferido ? 
                                                theme.palette.inferred.main :
                                                theme.palette.success.main
                                        })}
                                    />

                                ) : (
                                    <RadioButtonUncheckedIcon
                                        color="disabled"
                                        sx={{
                                            fontSize: 22,
                                            color: 'inherit'
                                        }}
                                    />

                                )}

                                <Tooltip title={pointTooltip}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: passou ? 600 : 400,
                                            opacity: passou ? 1 : 0.6,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontSize: 18
                                        }}
                                    >
                                        {pontoDesc}
                                    </Typography>
                                </Tooltip>
                            </Box>

                            {/* PREVISTO */}

                            <Typography
                                variant="body2"
                                align="right"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: 18,
                                    color: 'inherit'
                                }}
                            >
                                {
                                    isLastPoint ? 
                                    formatHora(DATAHORA_FINAL_PREVISTO, 'HH:mm') :
                                    horarioPrevistoPonto(currentTrip?.DATAHORA_INICIAL_PREVISTO, ponto.TEMPO_CHEGADA)
                                }
                            </Typography>

                            {/* REAL */}
                            <Typography
                                variant="body2"
                                align="right"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: passou ? 600 : 400,
                                    fontSize: 18,
                                    color: 'inherit'
                                }}
                            >
                                {horarioPassagem}
                            </Typography>

                            {/* STATUS */}
                            <Typography
                                variant="body2"
                                align="right"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: passou ? 600 : 400,
                                    fontSize: 18,
                                    color: 'inherit'
                                }}
                            >
                                {labelPonto}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, paddingX: isMapMode ? 2 : 1, paddingY: isMapMode ? 1 : 0 }}>
                <Typography variant='body2'>{progressoPercurso}%</Typography>
                <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress variant="determinate" color={barColor} value={progressoPercurso} sx={{ height: 6, borderRadius: 5 }} />
                </Box>
                {isMapMode && (
                    <TripStatusSummary
                        statusTrip={statusTrip}
                        barColor={barColor}
                        situacao={situacao}
                        detaTrip={detaTrip}
                        hrInicio={hrInicio}
                        atendP={atendP}
                        totalP={totalP}
                        progressoPontos={progressoPontos}
                        kmsRodados={kmsRodados}
                        kmTotalRota={kmTotalRota}
                        diferencaKm={diferencaKm}
                    />
                )}
            </Box>
        </Box>
    );
}